/* Program Name: ActionColorButtons.js
 **  Description: several pre-styled MUI buttons that will be commonly used
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-03-17  STT  Initial program
 ** SD6391     2024-07-11  SGS  SD-6490 - Shopping - Read only feature
 **                             Added disabled version of secondary button
 ** SD9567    2025-04-16  sgs  SD9681 - CLV: Inventory Maintenance
 *********************************************************************************/

/**Material UI imports */
import { Button, Fab, IconButton } from '@mui/material' //DOCS: https://material-ui.com/styles/api/
import { styled } from '@mui/system'

/**Creating custom styled button for "ERROR" *red* colored button */
export const ErrorButton = styled(Button)(({ theme }) => ({
  height: 36,
  width: 120,
  margin: '8px 8px 8px 8px',
  whiteSpace: 'nowrap',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "SUCCESS" *green* colored button */
export const SuccessButton = styled(Button)(({ theme }) => ({
  height: 36,
  width: 120,
  margin: '8px 8px 8px 8px',
  whiteSpace: 'nowrap',
  color: '#f5f5f5',
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button */
export const SuccessButtonFlexWidth = styled(Button)(({ theme }) => ({
  height: 36,
  color: '#FFFFFF',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.light,
  },
}))

/**Creating custom styled full width button for "SUCCESS" *green* colored button */
export const SuccessButtonFull = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: '#f5f5f5',
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button */
export const PrimaryButtonFull = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: '#FFFFFF',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
}))

/**Creating custom styled button for "Secondary Actions" such as a  colored button */
export const SecondaryButtonFull = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: '#FFFFFF',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
}))

/**Creating custom styled button for "Secondary Actions" such as a  disabled button */
export const SecondaryButtonFullDisabled = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: '#FFFFFF',
  disabled: true,
  cursor: 'not-allowed',
  backgroundColor: theme.palette.secondary.main,
}))

/**Creating custom styled button for "Error Actions" such as a  colored button */
export const ErrorButtonFull = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button */
export const PrimaryButton = styled(Button)(({ theme }) => ({
  height: 36,
  width: 120,
  color: '#FFFFFF',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button */
export const PrimaryButtonFlexWidth = styled(Button)(({ theme }) => ({
  height: 36,
  color: '#FFFFFF',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
}))

/**Creating custom styled button for "Secondary Actions" such as a cancel colored button */
export const SecondaryButton = styled(Button)(({ theme }) => ({
  height: 36,
  width: 120,
  color: theme.palette.getContrastText(theme.palette.secondary.main),
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
}))

/**Creating custom styled button for disabled actions that span the entire length of the div */
export const GreyButtonFull = styled(Button)(({ theme }) => ({
  height: 36,
  width: '100%',
  color: '#D4D4D4',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.primary.light + '33',
  '&:hover': {
    backgroundColor: theme.palette.error.light,
  },
}))

/**Creating custom styled button for disabled actions with standard button sizes */
export const GreyButton = styled(Button)(({ theme }) => ({
  height: 36,
  width: 120,
  color: '#D4D4D4',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.primary.light + '33',
  '&:hover': {
    backgroundColor: theme.palette.error.light,
  },
}))

/**Creating custom styled button for "SUCCESS" *green* colored button that is square*/
export const SquareSuccessButton = styled(Button)(({ theme }) => ({
  maxWidth: 36,
  minWidth: 36,
  maxHeight: 36,
  minHeight: 36,
  color: '#f5f5f5',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Error" *red* colored button that is square*/
export const SquareErrorButton = styled(Button)(({ theme }) => ({
  maxWidth: 36,
  minWidth: 36,
  maxHeight: 36,
  minHeight: 36,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button that is square*/
export const SquarePrimaryButton = styled(Button)(({ theme }) => ({
  maxWidth: 36,
  minWidth: 36,
  maxHeight: 36,
  minHeight: 36,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.primary.light,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Secondary Actions" such as a cancel colored button that is square*/
export const SquareSecondaryButton = styled(Button)(({ theme }) => ({
  maxWidth: 36,
  minWidth: 36,
  maxHeight: 36,
  minHeight: 36,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "SUCCESS" *green* colored button that is square*/
export const SquareSuccessButtonSM = styled(Button)(({ theme }) => ({
  maxWidth: 32,
  minWidth: 32,
  maxHeight: 32,
  minHeight: 32,
  color: '#f5f5f5',
  margin: '8px 8px 8px 8px',
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Error" *red* colored button that is square*/
export const SquareErrorButtonSM = styled(Button)(({ theme }) => ({
  maxWidth: 32,
  minWidth: 32,
  maxHeight: 32,
  minHeight: 32,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Primary Actions" such as a cancel colored button that is square*/
export const SquarePrimaryButtonSM = styled(Button)(({ theme }) => ({
  maxWidth: 32,
  minWidth: 32,
  maxHeight: 32,
  minHeight: 32,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.primary.light,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))

/**Creating custom styled button for "Secondary Actions" such as a cancel colored button that is square*/
export const SquareSecondaryButtonSM = styled(Button)(({ theme }) => ({
  maxWidth: 32,
  minWidth: 32,
  maxHeight: 32,
  minHeight: 32,
  margin: '8px 8px 8px 8px',
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '&:disabled': {
    backgroundColor: '#efefef',
  },
}))
/**Creating custom styled button for Dangerous Actions that is Circle*/
export const ErrorFAB = styled(Fab)(({ theme }) => ({
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}))

/**Creating custom styled button for positive actions that is Circle*/
export const SuccessFAB = styled(Fab)(({ theme }) => ({
  color: theme.palette.success.contrastText,
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
}))

/**Creating custom styled button for actions that is primary color and circle*/
export const PrimaryFAB = styled(Fab)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}))

/**Creating custom styled button for actions that is secondary color and circle*/
export const SecondaryFAB = styled(Fab)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}))

/**Creating custom styled button for actions that is secondary color and circle*/
export const SmallSuccessFAB = styled(Fab)(({ theme }) => ({
  maxWidth: 30,
  minWidth: 30,
  maxHeight: 30,
  minHeight: 30,
  boxShadow: 'none',
  color: theme.palette.success.contrastText,
  backgroundColor: theme.palette.success.main,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
}))
/**Creating custom styled button for actions that is secondary color and circle*/
export const SmallErrorFAB = styled(Fab)(({ theme }) => ({
  maxWidth: 30,
  minWidth: 30,
  maxHeight: 30,
  minHeight: 30,
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}))

export const CounterButton = styled(IconButton)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxSizing: 'border-box',
  outline: 0,
  border: 0,
  margin: 0,
  cursor: 'pointer',
  userSelect: 'none',
  verticalAlign: 'vertical',
  appearance: 'none',
  textDecoration: 'none',
  textAlign: 'center',
  flex: '0 0 auto',
  borderRadius: '50%',
  overflow: 'visible',
  padding: 3,
  fontSize: '.5rem',
}))
